/**
 * Dropdown / MenuPopover — floating menu anchored to a trigger.
 *
 * Used by avatar dropdown, row action menus, table column menus.
 *
 * Recipe:
 *   - Anchors to a passed-in `HTMLElement` ref (or auto-anchors via children
 *     trigger). 8px offset below the anchor; smart-flips above when there isn't
 *     room.
 *   - Items: icon + label rows, optional separator, optional `danger` variant
 *     (coral text + soft coral hover).
 *   - Keyboard: ArrowDown / ArrowUp navigate, Enter / Space select, Esc closes,
 *     Tab moves focus out + closes.
 *   - Render via portal so dropdown escapes overflow + transforms.
 *   - Mount: opacity 0 + scale 0.97 + y -4 → settled (180ms).
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

/* ─────────────────────────────────────────── MenuPopover (raw floating panel) */

export interface MenuPopoverProps {
  open: boolean;
  onClose: () => void;
  /** Element to anchor against. The popover positions 8px below it. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Default 220. */
  minWidth?: number;
  /** Alignment along the anchor edge: 'start' (left), 'end' (right). */
  align?: "start" | "end";
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** aria role; defaults to "menu". */
  role?: "menu" | "listbox" | "dialog";
  /** aria-labelledby on the surface, if you supply one. */
  ariaLabelledBy?: string;
  /**
   * `below` (default) drops under the anchor and flips above when short of
   * room. `right` opens beside the anchor, bottom-aligned to it, for triggers
   * that live in the left rail.
   */
  placement?: "below" | "right";
}

interface Position {
  top: number;
  left: number;
  placement: "below" | "above";
}

export function MenuPopover({
  open,
  onClose,
  anchorRef,
  minWidth = 220,
  align = "end",
  className,
  style,
  children,
  role = "menu",
  ariaLabelledBy,
  placement: side = "below",
}: MenuPopoverProps) {
  const reduce = useReducedMotion();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);

  const recompute = useCallback(() => {
    const anchor = anchorRef.current;
    const surface = surfaceRef.current;
    if (!anchor || !surface) return;
    const a = anchor.getBoundingClientRect();
    const sh = surface.offsetHeight;
    const sw = surface.offsetWidth;
    const gap = 8;

    if (side === "right") {
      const left = Math.min(a.right + 12, window.innerWidth - sw - 8);
      const top = Math.max(8, Math.min(a.bottom - sh, window.innerHeight - sh - 8));
      setPos({ top, left, placement: "below" });
      return;
    }

    let placement: "below" | "above" = "below";
    let top = a.bottom + gap;
    if (top + sh > window.innerHeight - 12 && a.top - gap - sh > 12) {
      placement = "above";
      top = a.top - gap - sh;
    }

    let left =
      align === "start" ? a.left : a.right - Math.max(sw, minWidth);
    left = Math.max(8, Math.min(left, window.innerWidth - sw - 8));

    setPos({ top, left, placement });
  }, [align, anchorRef, minWidth, side]);

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const onResize = () => recompute();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    // Content that loads after opening (a feed replacing its skeleton) changes
    // the surface's height, so re-fit it to the viewport whenever it resizes.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (surfaceRef.current) observer?.observe(surfaceRef.current);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      observer?.disconnect();
    };
  }, [open, recompute]);

  // Click-outside + Esc + Tab close.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (
        surfaceRef.current?.contains(target) ||
        anchorRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        // allow Tab to flow out — close the menu so focus doesn't trap.
        onClose();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={surfaceRef}
          role={role}
          aria-labelledby={ariaLabelledBy}
          initial={{
            opacity: 0,
            scale: reduce ? 1 : 0.97,
            y: reduce ? 0 : -4,
          }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{
            opacity: 0,
            scale: reduce ? 1 : 0.97,
            y: reduce ? 0 : -4,
          }}
          transition={{ duration: reduce ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] }}
          className={className}
          style={{
            position: "fixed",
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            minWidth,
            // Above drawers and modals, so a menu opened inside one shows.
            zIndex: "calc(var(--z-toast) - 1)" as unknown as number,
            background: "var(--float-bg)",
            backdropFilter: "var(--float-blur)",
            WebkitBackdropFilter: "var(--float-blur)",
            border: "1px solid var(--float-border)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--float-shadow)",
            padding: 6,
            outline: "none",
            ...style,
          }}
          onKeyDown={(e) => handleMenuKeyNav(e, surfaceRef.current)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/* ─────────────────────────────────────────── Dropdown (trigger + menu) */

export interface DropdownItem {
  id?: string;
  label: ReactNode;
  icon?: ComponentType<{ size?: number; strokeWidth?: number }>;
  /** Renders a coral-tinted item. Use for destructive actions. */
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  /** Renders as a divider; ignores all other fields. */
  separator?: boolean;
}

export interface DropdownProps {
  /** A render-prop that returns the trigger element. Pass through the ref + open state. */
  trigger: (args: {
    ref: RefObject<HTMLButtonElement | null>;
    open: boolean;
    toggle: () => void;
  }) => ReactNode;
  items: DropdownItem[];
  align?: "start" | "end";
  minWidth?: number;
  placement?: "below" | "right";
  /** Optional header rendered above the items, e.g. the signed-in account. */
  header?: ReactNode;
}

export function Dropdown({
  trigger,
  items,
  align = "end",
  minWidth = 220,
  placement,
  header,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const labelId = useId();

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  const triggerNode = useMemo(
    () => trigger({ ref: anchorRef, open, toggle }),
    [trigger, anchorRef, open, toggle],
  );

  return (
    <>
      {triggerNode}
      <MenuPopover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        align={align}
        minWidth={minWidth}
        ariaLabelledBy={labelId}
        placement={placement}
      >
        {header}
        {items.map((item, i) =>
          item.separator ? (
            <div
              key={`sep-${i}`}
              role="separator"
              style={{
                height: 1,
                background: "var(--color-border-subtle)",
                marginBlock: 4,
              }}
            />
          ) : (
            <MenuItem
              key={item.id ?? `${typeof item.label === "string" ? item.label : "item"}-${i}`}
              item={item}
              onClose={close}
            />
          ),
        )}
      </MenuPopover>
    </>
  );
}

function MenuItem({
  item,
  onClose,
}: {
  item: DropdownItem;
  onClose: () => void;
}) {
  const Icon = item.icon;
  const danger = !!item.danger;
  const disabled = !!item.disabled;

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        item.onSelect?.();
        onClose();
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLButtonElement).style.background = danger
          ? "var(--color-danger-soft)"
          : "var(--color-accent-soft)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
      style={{
        width: "100%",
        minHeight: 36,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        borderRadius: "var(--radius-sm)",
        font: "500 13px/1.2 var(--font-sans)",
        color: disabled
          ? "var(--color-faint)"
          : danger
            ? "var(--color-danger)"
            : "var(--color-primary)",
        textAlign: "left",
        background: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background-color var(--duration-fast) var(--ease-smooth)",
      }}
    >
      {Icon ? <Icon size={16} strokeWidth={1.75} /> : null}
      <span style={{ flex: 1 }}>{item.label}</span>
    </button>
  );
}

/* ─────────────────────────────────────────── Keyboard nav helper */

function handleMenuKeyNav(
  e: ReactKeyboardEvent<HTMLDivElement>,
  root: HTMLDivElement | null,
): void {
  if (!root) return;
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") {
    return;
  }
  e.preventDefault();
  const items = Array.from(
    root.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not([disabled])'),
  );
  if (items.length === 0) return;
  const active = document.activeElement;
  const idx = items.findIndex((el) => el === active);
  let next = 0;
  if (e.key === "ArrowDown") next = idx < items.length - 1 ? idx + 1 : 0;
  else if (e.key === "ArrowUp") next = idx > 0 ? idx - 1 : items.length - 1;
  else if (e.key === "Home") next = 0;
  else if (e.key === "End") next = items.length - 1;
  items[next]?.focus();
}
